// src/hooks/useShipmentCharges.js
//
// The merchant statement for one shipment: GET /v3/oms/merchant/order/charges?orderReferenceNumber=…
//
// Returns { loading, error, data, retry }.
//   error: null
//        | { kind: "not-found", status }       — 404: the reference is not this merchant's (quiet state, no retry)
//        | { kind: "request-failed", status }  — 403 / 5xx / network / no session (error state with retry)
//   data:  the snake_case wire payload normalised to camelCase with safe defaults, so the card never
//          has to guard against a missing `summary` or a non-array `items`.
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { API } from "../utils/apiUrls";

const REQUEST_TIMEOUT_MS = 30000;

const upper = (value, fallback) =>
  value == null || value === "" ? fallback : String(value).toUpperCase();

const normaliseItem = (item) => ({
  reason: upper(item?.reason, "OTHER"),
  direction: upper(item?.direction, "DEBIT"),
  amount: item?.amount ?? null,
  transactionDateTimeUtc: item?.transaction_date_time_utc ?? null,
  transactionId: item?.transaction_id ?? null,
  pending: Boolean(item?.pending),
  description: item?.description ?? null,
});

const normaliseRefund = (refund) =>
  refund && typeof refund === "object"
    ? {
        state: upper(refund.state, null),
        amount: refund.amount ?? null,
        refundedDateTimeUtc: refund.refunded_date_time_utc ?? null,
        transactionId: refund.transaction_id ?? null,
      }
    : null;

/** Wire payload → the shape ShipmentChargesCard renders. Exported for tests; pure. */
export const normaliseShipmentCharges = (raw) => {
  const summary = raw?.summary && typeof raw.summary === "object" ? raw.summary : {};
  return {
    orderReferenceNumber: raw?.order_reference_number ?? null,
    awbNumber: raw?.awb_number ?? null,
    shipmentType: upper(raw?.shipment_type, null),
    currencyCode: raw?.currency_code ?? "INR",
    settlementType: upper(raw?.settlement?.type, "UNKNOWN"),
    items: (Array.isArray(summary.items) ? summary.items : []).map(normaliseItem),
    refund: normaliseRefund(summary.refund),
    netCost: summary.net_cost ?? null,
  };
};

export default function useShipmentCharges(orderReferenceNumber) {
  const [attempt, setAttempt] = useState(0);
  // No reference → nothing to fetch: start (and stay) in the quiet not-found state, never a null-data render.
  const [state, setState] = useState(() =>
    orderReferenceNumber
      ? { loading: true, data: null, error: null }
      : { loading: false, data: null, error: { kind: "not-found", status: null } }
  );
  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  useEffect(() => {
    if (!orderReferenceNumber) {
      setState({ loading: false, data: null, error: { kind: "not-found", status: null } });
      return undefined;
    }
    // Same session keys and raw-token header as every other OMS call on the shipment page.
    const authToken = localStorage.getItem("authToken");
    const merchantId = localStorage.getItem("merchantId");
    if (!authToken || !merchantId) {
      setState({ loading: false, data: null, error: { kind: "request-failed", status: null } });
      return undefined;
    }

    const controller = new AbortController();
    setState({ loading: true, data: null, error: null });
    axios
      .get(`${API.merchantOrderCharges()}?orderReferenceNumber=${encodeURIComponent(orderReferenceNumber)}`, {
        headers: { Authorization: authToken, "x-merchant-id": merchantId },
        signal: controller.signal,
        timeout: REQUEST_TIMEOUT_MS,
      })
      .then((res) => {
        if (controller.signal.aborted) return;
        setState({ loading: false, data: normaliseShipmentCharges(res.data), error: null });
      })
      .catch((err) => {
        if (controller.signal.aborted || axios.isCancel(err)) return;
        const status = err?.response?.status ?? null;
        setState({
          loading: false,
          data: null,
          error: { kind: status === 404 ? "not-found" : "request-failed", status },
        });
      });
    return () => controller.abort();
  }, [orderReferenceNumber, attempt]);

  return { loading: state.loading, error: state.error, data: state.data, retry };
}
